import { readable, type Readable, type Unsubscriber } from "svelte/store";
import {
  collection,
  doc,
  getFirestore,
  onSnapshot,
  type DocumentData,
  query,
  orderBy,
  limit,
  where,
  type WhereFilterOp,
  type Query,
  type QueryConstraint,
  startAfter,
  FirestoreError,
} from "firebase/firestore";
import { firebaseApp } from "./env";
import { toaster } from "./toaster";

export function getDocumentAsStore<T extends object>({
  docPath,
  extraData = {} as any,
  onError,
}: {
  docPath: string;
  extraData?: T;
  onError?: (err: FirestoreError) => ({ id: string; data: DocumentData } & T) | undefined;
}): Readable<({ id: string; data: DocumentData } & T) | undefined> {
  return readable<({ id: string; data: DocumentData } & T) | undefined>(undefined, (set) => {
    let unsubDocument: Unsubscriber;
    firebaseApp.then((firebaseApp) => {
      const firestore = getFirestore(firebaseApp);
      unsubDocument = onSnapshot(
        doc(firestore, docPath),
        (snapshot) => {
          set({
            id: snapshot.id,
            data: snapshot.data() ?? {},
            ...extraData,
          });
        },
        (err) => {
          if (onError) {
            set(onError(err));
          }
        },
      );
    });
    return () => {
      if (unsubDocument) {
        unsubDocument();
      }
    };
  });
}

/**
 * getPaginatedDocumentAsStore works like getDocumentAsStore, but it will keep fetching the next page of documents until there are no more pages.
 * It will merge the data from each page into a single object, allowing you to access all the data as if it were a single document.
 *
 * @param docPath the base path of all documents
 * @param docId the id of the document to start with
 * @param extraData any extra data to merge into the document store
 * @param onError a function that will be called if there is an error fetching the document. It should return an object with the id and data of the document, or undefined if the error should not be handled.
 * @param paginationKey the key in the document data that contains the id of the next page. Defaults to "nextPageId".
 * @param mergeKeys an array of keys that should be merged from each page into the final document. If a key is not present in a page, it will not be added to the final document.
 * @returns
 */
export function getPaginatedDocumentAsStore<T extends object>({
  docPath,
  docId,
  extraData = {} as any,
  onError,
  paginationKey = "nextPageId",
  mergeKeys = [],
}: {
  docPath: string;
  docId: string;
  extraData?: T;
  onError?: (err: FirestoreError) => ({ id: string; data: DocumentData } & T) | undefined;
  paginationKey: string;
  mergeKeys: string[];
}): Readable<({ id: string; data: DocumentData } & T) | undefined> {
  const documents: Record<string, DocumentData> = {};

  function mergeKeySet(key: string): DocumentData {
    const mergedData: DocumentData = {
      [key]: [],
    };

    let nextId = docId;

    while (nextId) {
      const docData = documents[nextId];
      if (docData && key in docData) {
        mergedData[key] = [...mergedData[key], ...docData[key]];
      }
      nextId = docData ? docData[paginationKey] : undefined;
    }

    return mergedData;
  }

  return readable<({ id: string; data: DocumentData } & T) | undefined>(undefined, (set) => {
    // This is the list of unsubscriptions for each page
    const unsubs: Unsubscriber[] = [];

    // Next page id to fetch
    let nextPageId: string | undefined = docId;

    (async () => {
      do {
        nextPageId = await new Promise<string | undefined>((resolve) => {
          const unsub = getDocumentAsStore({ docPath: `${docPath}/${nextPageId}`, extraData, onError }).subscribe(
            (doc) => {
              if (!doc) {
                console.warn(`[getPaginatedDocumentAsStore] Document not found at ${docPath}/${nextPageId}`);
                return;
              }

              documents[doc.id] = doc.data;

              set({
                id: docId,
                data: {
                  ...documents[docId],
                  ...Object.assign({}, ...mergeKeys.map((key) => mergeKeySet(key))),
                },
                ...extraData,
              });

              resolve(doc.data[paginationKey]);
            },
          );
          unsubs.push(unsub);
        });
      } while (nextPageId);
    })();

    return () => {
      unsubs.forEach((u) => u());
    };
  });
}

export function mergeLatestOfEachToArray<T>(inputs: Readable<T>[]): Readable<T[]> {
  return readable<T[]>([], (set) => {
    let state: { [i: number]: T } = {};
    const unsubs = inputs.map((read, index, _arr) => {
      return read.subscribe((item) => {
        state[index] = item;
        set(Object.values(state));
      });
    });
    return () => {
      unsubs.forEach((u) => u());
    };
  });
}

export function chainStores<T, U>(
  outer: Readable<T | undefined>,
  initialInnerValue: U,
  func: (a: T) => Readable<U>,
  resetToInitialWhenOuterIsUndefined: boolean = true,
): Readable<U> {
  return readable<U>(initialInnerValue, (set) => {
    let innerUnsub: Unsubscriber | undefined;
    const outerUnsub = outer.subscribe((outerValue) => {
      if (innerUnsub) {
        innerUnsub();
      }
      if (outerValue !== undefined) {
        innerUnsub = func(outerValue).subscribe((innerValue) => {
          set(innerValue);
        });
      } else if (resetToInitialWhenOuterIsUndefined) {
        set(initialInnerValue);
      }
    });
    return () => {
      if (innerUnsub) {
        innerUnsub();
      }
      outerUnsub();
    };
  });
}

export interface QueryOptions {
  orderBy?: { field: string; direction: "asc" | "desc" }[];
  limit?: number;
  where?: { field: string; op: WhereFilterOp; value: any }[];
  startAfter?: { [field: string]: any };
}

export function getSubCollectionAsStore(
  docPath: string,
  options?: QueryOptions,
  onError?: (err: FirestoreError) => { id: string; data: DocumentData }[],
): Readable<{ id: string; data: DocumentData }[]> {
  return readable<{ id: string; data: DocumentData }[]>([], (set) => {
    let unsubCollection: Unsubscriber;
    firebaseApp.then((firebaseApp) => {
      const firestore = getFirestore(firebaseApp);
      const collectionRef = collection(firestore, docPath);
      const constraints: QueryConstraint[] = [];

      if (options?.orderBy) {
        options.orderBy.forEach(({ field, direction }) => {
          constraints.push(orderBy(field, direction));
        });
      }

      if (options?.where) {
        options.where.forEach(({ field, op, value }) => {
          constraints.push(where(field, op, value));
        });
      }

      if (options?.limit) {
        constraints.push(limit(options.limit));
      }

      if (options?.startAfter && options.orderBy) {
        const values = options.orderBy.map(({ field }) => options.startAfter![field]);
        constraints.push(startAfter(...values));
      }

      const queryRef = constraints.length > 0 ? query(collectionRef, ...constraints) : collectionRef;

      const fetchStartTime = performance.now();
      unsubCollection = onSnapshot(
        queryRef,
        (col) => {
          console.log("[getSubCollectionAsStore] ** COL **", col.docs.length, performance.now() - fetchStartTime);
          const startTime = performance.now();
          const items: { id: string; data: DocumentData }[] = [];
          col.forEach((snapshot) => {
            items.push({
              id: snapshot.id,
              data: snapshot.data(),
            });
          });
          if (!options?.orderBy) {
            items.sort((a, b) => a.id.localeCompare(b.id));
          }
          const endTime = performance.now();
          console.log("[getSubCollectionAsStore] ** Time taken **", endTime - startTime);
          set(items);
        },
        (err) => {
          if (onError) {
            set(onError(err));
          }
        },
      );
    });
    return () => {
      if (unsubCollection) {
        unsubCollection();
      }
    };
  });
}
